// src/app/services/activity-log.service.ts
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom, take } from 'rxjs';
import { environment } from '../../environments/environment';

import { AuthService } from '../auth/services/auth.service'; //

/**
 * Service responsible for logging application activities and events.
 * Provides functionality to:
 * - Log user events and actions
 * - Track navigation events
 * - Record error occurrences
 * - Maintain audit trails with user context
 * 
 * @description
 * This service integrates with a backend logging system to maintain
 * comprehensive activity logs with user context and detailed event information.
 */

// Define the structure for log payloads
interface LogPayload {
  timestamp: string;
  userId?: string;       // Keycloak 'sub' claim
  userEmail?: string;    // Keycloak 'email' claim
  eventName: string;     // e.g., 'LocationConfirmed', 'SourceScraped'
  eventType: string;     // e.g., 'ui_interaction', 'navigation', 'api_call', 'error'
  component?: string;    // Optional: Angular component name
  currentRoute?: string; // The route where the event occurred
  details?: Record<string, any>; // Any additional relevant JSON data
  userAgent?: string;    // Browser/OS info
  sessionId?: string;    // Optional: Session identifier
}

@Injectable({
  providedIn: 'root',
})
export class ActivityLogService {
  // Inject necessary services
  private http = inject(HttpClient);
  private authService = inject(AuthService); //
  private router = inject(Router);

  // Get the logging API URL from environment configuration
  private loggingApiUrl = environment.loggingApiUrl; // Placeholder for your Go API
  private disableLogging = environment.disableLogging;

  // --- Private Helper to Get User Context ---

  private async getUserContext(): Promise<{ userId?: string; userEmail?: string }> {
    try {
      const isAuthenticated = await firstValueFrom(this.authService.isAuthenticated().pipe(take(1)));
      if (!isAuthenticated) {
        return {};
      }

      try {
        const profile = await firstValueFrom(this.authService.getUserProfile().pipe(take(1)));
        return {
          userId: profile?.sub || profile?.id || profile?.userId,
          userEmail: profile?.email || profile?.userEmail,
        };
      } catch (error) {
        return {};
      }
    } catch (error) {
      return {};
    }
  }

  // --- Public Logging Methods ---

  /**
   * Logs a general user event or action.
   * @param eventName - A descriptive name for the event
   * @param eventType - The category of the event
   * @param details - Optional additional data related to the event
   * @param component - Optional name of the component where the event originated
   */
  async logEvent(
    eventName: string,
    eventType: string,
    details: Record<string, any> = {},
    component?: string
  ): Promise<void> {
    const userContext = await this.getUserContext();
    const payload: LogPayload = {
      timestamp: new Date().toISOString(),
      userId: userContext.userId,
      userEmail: userContext.userEmail,
      eventName,
      eventType,
      component,
      currentRoute: this.router.url,
      details,
      userAgent: navigator.userAgent
    };
    this.sendLog(payload);
  }

  /**
   * Logs a navigation event when the user moves between routes.
   * @param url - The URL the user navigated to
   */
  async logNavigation(url: string): Promise<void> {
    const userContext = await this.getUserContext();
    const payload: LogPayload = {
      timestamp: new Date().toISOString(),
      userId: userContext.userId,
      userEmail: userContext.userEmail,
      eventName: 'Navigation',
      eventType: 'navigation',
      currentRoute: url,
      details: { navigatedTo: url },
      userAgent: navigator.userAgent
    };
    this.sendLog(payload);
  }

  /**
   * Logs a frontend error.
   * @param error - The Error object caught
   * @param context - Optional description of where the error occurred
   */
  async logError(error: Error, context?: string): Promise<void> {
    const userContext = await this.getUserContext();
    const payload: LogPayload = {
      timestamp: new Date().toISOString(),
      userId: userContext.userId,
      userEmail: userContext.userEmail,
      eventName: 'FrontendError',
      eventType: 'error',
      currentRoute: this.router.url,
      details: {
        message: error.message,
        stack: error.stack,
        context: context || 'Unknown',
      },
      userAgent: navigator.userAgent
    };
    this.sendLog(payload);
  }

  // --- Private Method to Send Log ---

  private sendLog(payload: LogPayload): void {
    // Skip logging if disabled or if the API URL is not properly configured
    if (this.disableLogging || !this.loggingApiUrl || this.loggingApiUrl === '/api/logging') {
      return;
    }

    this.http.post(`${this.loggingApiUrl}/log`, payload, {
      headers: { 'Content-Type': 'application/json' }
    }).subscribe({
      error: (err) => {
        this.logEvent(
          'LogSendFailed',
          'error',
          {
            status: err.status,
            statusText: err.statusText,
            message: err.message,
            url: err.url
          }
        );
      }
    });
  }

  // --- Optional: Session ID Logic ---
  /*
  private getSessionId(): string {
    // Implement logic to get or generate a unique session ID
    // e.g., using sessionStorage
    let sessionId = sessionStorage.getItem('appSessionId');
    if (!sessionId) {
      sessionId = crypto.randomUUID(); // Modern browser UUID generation
      sessionStorage.setItem('appSessionId', sessionId);
    }
    return sessionId;
  }
  */
}